import React, { useMemo, useState } from "react"
import moment from "moment"

// UI imports
import Button from "../../../components/ui/button/button"
import Datepicker from "../../../components/ui/datepicker/datepicker"
import Navigation from "../../../components/ui/navigation/navigation"
import Table from "../../../components/structure/table/table"
import SupplierPicker from "../_components/supplier-picker"

// Audit pipeline
import { loadSupplierAudit } from "../_utils/supplier-audit/load"
import { buildWorkbookSheets, buildSupplierWorkbookSheets } from "../_utils/supplier-audit/rows"
import { supplierKey } from "../_utils/supplier-audit/types"
import { planSupplierPacks, slugify } from "../_utils/supplier-audit/packs"
import { canWriteFolders, pickParentFolder, writeSupplierPacks } from "../_utils/supplier-audit/write-folder"
import { exportSheetsToExcel } from "../_utils/export"

// Default window start: the run-off team's cut-off (see docs/supplier-audit-export.md)
const DEFAULT_START = moment("2026-10-01").startOf("day").valueOf()

// Rows shown per preview tab
const PREVIEW_ROWS = 50

/**
 * Render one workbook cell value for the preview table. Dates come through as
 * JS Date objects, numbers are formatted for reading and blanks show as a dash.
 */
const renderCell = (value) => {
	if (value === "" || value === null || value === undefined) return <span className="db-exports-muted">-</span>
	if (value instanceof Date) {
		const date = moment(value)
		const hasTime = date.hours() !== 0 || date.minutes() !== 0
		return date.format(hasTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY")
	}
	if (typeof value === "number") return value.toLocaleString()
	return String(value)
}

/**
 * "Supplier audit" tab - builds the audit workbook Golfbreaks asked for: one
 * row per booking in the layout of Jude's template, plus per-type tabs for
 * hotels, golf, transport and the suppliers themselves. Each supplier can also
 * be downloaded on its own so the office can send it straight to them.
 */
export default function SupplierAudit() {
	// Window (milliseconds). The end date is optional; null means open-ended.
	const [startDate, setStartDate] = useState(DEFAULT_START)
	const [endDate, setEndDate] = useState(null)
	// Remount key for the end date picker, which keeps its own internal value
	// and cannot otherwise be cleared once one has been chosen
	const [endPickerKey, setEndPickerKey] = useState(0)

	// Loading / progress / error state
	const [loading, setLoading] = useState(false)
	const [progress, setProgress] = useState({ done: 0, total: 0 })
	const [error, setError] = useState("")

	// The assembled audit (AuditResult) once generated
	const [result, setResult] = useState(null)

	// Supplier download + preview state
	const [selectedSuppliers, setSelectedSuppliers] = useState([])
	const [previewTab, setPreviewTab] = useState("Bookings")

	// Save-all-packs state
	const [saving, setSaving] = useState(false)
	const [saveProgress, setSaveProgress] = useState({ done: 0, total: 0 })
	const [saveMessage, setSaveMessage] = useState({ type: "", text: "" })

	// The five workbook sheets, shaped once per result and shared by the
	// preview and the full export so they can never drift apart
	const sheets = useMemo(() => (result ? buildWorkbookSheets(result) : []), [result])

	// Every supplier grouped into the packs the folder export writes
	const plan = useMemo(() => (result ? planSupplierPacks(result) : null), [result])
	const planCounts = useMemo(() => {
		if (!plan) return null
		const count = (type) => plan.packs.filter((pack) => pack.type === type).length
		return { hotel: count("hotel"), course: count("course"), transfer: count("transfer"), checks: plan.checks.length }
	}, [plan])

	// Headline figures for the summary line
	const summary = useMemo(() => {
		if (!result) return null

		const hotels = result.bookings.flatMap((booking) => booking.hotels)
		const rounds = result.bookings.flatMap((booking) => booking.rounds)
		const transportSheet = sheets.find((sheet) => sheet.name === "Transport")

		const start = moment(result.window.startMillis).format("D MMM YYYY")
		const windowLabel = result.window.endMillis ? `${start} - ${moment(result.window.endMillis).format("D MMM YYYY")}` : `${start} onwards`

		return {
			scanned: result.scanned,
			hotelLines: hotels.length,
			hotelsConfirmed: hotels.filter((hotel) => hotel.supplierConfirmed).length,
			rounds: rounds.length,
			roundsConfirmed: rounds.filter((round) => round.supplierConfirmed).length,
			transportBookings: transportSheet ? transportSheet.rows.length : 0,
			suppliers: result.suppliers.length,
			supplierGaps: result.suppliers.filter((supplier) => !supplier.region || !supplier.country).length,
			windowLabel,
		}
	}, [result, sheets])

	const clearEndDate = () => {
		setEndDate(null)
		setEndPickerKey((key) => key + 1)
	}

	/**
	 * Read every booking in the window and assemble the audit.
	 */
	const generate = async () => {
		if (!startDate || loading) return

		setLoading(true)
		setError("")
		setResult(null)
		setSelectedSuppliers([])
		setPreviewTab("Bookings")
		setSaveMessage({ type: "", text: "" })
		setProgress({ done: 0, total: 0 })

		try {
			const audit = await loadSupplierAudit({
				startMillis: moment(startDate).startOf("day").valueOf(),
				endMillis: endDate ? moment(endDate).endOf("day").valueOf() : null,
				onProgress: setProgress,
			})
			setResult(audit)
		} catch (err) {
			console.error("Error generating supplier audit:", err)
			setError("The audit could not be generated. Check the console for details and try again.")
		} finally {
			setLoading(false)
		}
	}

	// File-name fragment for the window, e.g. "2026-10-01" or "2026-10-01_to_2026-12-31"
	const windowSlug = (range) => {
		const start = moment(range.startMillis).format("YYYY-MM-DD")
		return range.endMillis ? `${start}_to_${moment(range.endMillis).format("YYYY-MM-DD")}` : start
	}

	/**
	 * Download the full five-tab workbook.
	 */
	const exportWorkbook = () => {
		if (!result) return

		const start = moment(result.window.startMillis).format("YYYY-MM-DD")
		const suffix = result.window.endMillis ? `_to_${moment(result.window.endMillis).format("YYYY-MM-DD")}` : ""
		exportSheetsToExcel(sheets, `supplier_audit_${start}_onwards${suffix}`)
	}

	/**
	 * Write every pack plus _index.xlsx into a new folder inside the one the
	 * user picks, ready to zip up and send.
	 */
	const saveAllPacks = async () => {
		if (!result || !plan || saving) return

		let parent
		try {
			parent = await pickParentFolder()
		} catch (err) {
			if (err.name !== "AbortError") setSaveMessage({ type: "error", text: "The folder picker could not be opened." })
			return
		}

		const folderName = `supplier_audit_${windowSlug(result.window)}`
		setSaving(true)
		setSaveMessage({ type: "", text: "" })
		setSaveProgress({ done: 0, total: plan.packs.length + 1 })

		try {
			await writeSupplierPacks({ parent, folderName, result, plan, onProgress: setSaveProgress })
			setSaveMessage({ type: "done", text: `Saved ${plan.packs.length.toLocaleString()} packs and _index.xlsx into "${folderName}" inside "${parent.name}".` })
		} catch (err) {
			console.error("Error saving supplier packs:", err)
			setSaveMessage({ type: "error", text: err.name === "FolderExistsError" ? err.message : "The packs could not all be saved. Check the console for details and try again." })
		} finally {
			setSaving(false)
		}
	}

	/**
	 * Download a workbook holding only the chosen suppliers' lines. Picking a
	 * hotel together with its courses gives one pack for the whole resort. The
	 * file is named after the first supplier chosen, with a count when there
	 * are more.
	 */
	const exportSuppliers = () => {
		if (!result || selectedSuppliers.length === 0) return

		const chosen = new Set(selectedSuppliers)
		const suppliers = result.suppliers.filter((candidate) => chosen.has(supplierKey(candidate)))
		if (suppliers.length === 0) return

		const start = moment(result.window.startMillis).format("YYYY-MM-DD")
		const label = suppliers.length > 1 ? `${slugify(suppliers[0].name)}_and_${suppliers.length - 1}_more` : slugify(suppliers[0].name)
		exportSheetsToExcel(buildSupplierWorkbookSheets(result, selectedSuppliers), `supplier_audit_${label}_${start}`)
	}

	// Preview data for the active sheet
	const activeSheet = sheets.find((sheet) => sheet.name === previewTab) || sheets[0]
	const previewHeaders = activeSheet ? activeSheet.headers || Object.keys(activeSheet.rows[0] || {}) : []
	const previewRows = activeSheet ? activeSheet.rows.slice(0, PREVIEW_ROWS) : []

	return (
		<>
			<p className="db-exports-intro">
				Build the supplier audit workbook for bookings travelling from the chosen date onwards. It contains five tabs: Bookings (one row per booking in the layout of the Golfbreaks template),
				Hotels, Golf and Transport (one row per supplier line) and Suppliers (every hotel, course and transfer company in the window, with any region or country gaps to fill in the CMS). Any
				single supplier can also be downloaded on its own.
			</p>

			<div className="page-filters db-exports-filters db-exports-audit-filters">
				<Datepicker
					value={startDate}
					onSelect={(date) => setStartDate(date)}
					placeholder="From (travel date)"
				/>

				<div className="db-exports-audit-end-date">
					<Datepicker
						key={endPickerKey}
						value={endDate}
						onSelect={(date) => setEndDate(date)}
						placeholder="To (optional)"
					/>
					{endDate && (
						<button
							type="button"
							className="db-exports-audit-clear"
							onClick={clearEndDate}>
							Clear
						</button>
					)}
				</div>

				<Button
					filters={true}
					disabled={!startDate}
					label="Generate audit"
					loadingText="Generating..."
					loading={loading}
					onClick={generate}
				/>

				<Button
					filters={true}
					disabled={!result}
					label="Export workbook"
					onClick={exportWorkbook}
				/>
			</div>

			{/* Progress / error / summary */}
			{loading && (
				<p className="db-exports-summary">
					Reading bookings... {progress.done}
					{progress.total > 0 ? ` / ${progress.total}` : ""}
				</p>
			)}

			{!loading && error && <p className="db-exports-summary db-exports-audit-error">{error}</p>}

			{!loading && summary && (
				<div className="db-exports-audit-summary">
					<p className="db-exports-summary">
						Scanned <strong>{summary.scanned.toLocaleString()}</strong> active booking{summary.scanned === 1 ? "" : "s"} travelling <strong>{summary.windowLabel}</strong>.
					</p>

					<div className="db-exports-audit-stats">
						<div className="db-exports-audit-stat">
							<span>Hotel lines</span>
							<strong>{summary.hotelLines.toLocaleString()}</strong>
							<small>
								{summary.hotelsConfirmed.toLocaleString()} confirmed, {(summary.hotelLines - summary.hotelsConfirmed).toLocaleString()} not
							</small>
						</div>
						<div className="db-exports-audit-stat">
							<span>Rounds</span>
							<strong>{summary.rounds.toLocaleString()}</strong>
							<small>
								{summary.roundsConfirmed.toLocaleString()} confirmed, {(summary.rounds - summary.roundsConfirmed).toLocaleString()} not
							</small>
						</div>
						<div className="db-exports-audit-stat">
							<span>Bookings with transport</span>
							<strong>{summary.transportBookings.toLocaleString()}</strong>
							<small>transfers or flights</small>
						</div>
						<div className="db-exports-audit-stat">
							<span>Suppliers</span>
							<strong>{summary.suppliers.toLocaleString()}</strong>
							<small className={summary.supplierGaps > 0 ? "is-warning" : ""}>{summary.supplierGaps.toLocaleString()} with region or country gaps</small>
						</div>
					</div>
				</div>
			)}

			{/* Every pack into one folder */}
			{result && planCounts && (
				<div className="db-exports-section db-exports-audit-supplier">
					<div className="db-exports-section-header">
						<h2>
							Save every supplier pack <span className="db-exports-count">({plan.packs.length.toLocaleString()} packs)</span>
						</h2>
					</div>

					<p className="db-exports-intro">
						Writes a <strong>supplier_audit_{windowSlug(result.window)}</strong> folder holding <strong>_index.xlsx</strong> and three sub-folders: hotels ({planCounts.hotel.toLocaleString()}), golf_courses (
						{planCounts.course.toLocaleString()}) and transfers ({planCounts.transfer.toLocaleString()}). One workbook per supplier, with records for the same venue ("The Belfry" and "The Belfry Groups
						16+") sharing a pack. The index lists every pack and {planCounts.checks.toLocaleString()} supplier{planCounts.checks === 1 ? "" : "s"} worth checking before it goes out. Zip the folder to
						send it.
					</p>

					{canWriteFolders() ? (
						<div className="db-exports-audit-supplier-row">
							<Button
								filters={true}
								disabled={saving}
								label="Choose a location and save"
								loadingText={`Saving ${saveProgress.done.toLocaleString()} / ${saveProgress.total.toLocaleString()}...`}
								loading={saving}
								onClick={saveAllPacks}
							/>
						</div>
					) : (
						<p className="db-exports-summary db-exports-audit-error">Saving into a folder needs Chrome or Edge.</p>
					)}

					{saveMessage.text && <p className={`db-exports-summary ${saveMessage.type === "error" ? "db-exports-audit-error" : ""}`}>{saveMessage.text}</p>}
				</div>
			)}

			{/* Per-supplier download */}
			{result && (
				<div className="db-exports-section db-exports-audit-supplier">
					<div className="db-exports-section-header">
						<h2>
							Download for chosen suppliers <span className="db-exports-count">({result.suppliers.length.toLocaleString()} suppliers in this window)</span>
						</h2>
					</div>

					<p className="db-exports-intro">Pick a hotel together with its courses to get one workbook for the whole resort.</p>

					<div className="db-exports-audit-supplier-row">
						<SupplierPicker
							suppliers={result.suppliers}
							value={selectedSuppliers}
							onChange={setSelectedSuppliers}
						/>

						<Button
							filters={true}
							disabled={selectedSuppliers.length === 0}
							label={selectedSuppliers.length > 1 ? `Download workbook (${selectedSuppliers.length} suppliers)` : "Download supplier workbook"}
							onClick={exportSuppliers}
						/>
					</div>
				</div>
			)}

			{/* Preview of each workbook tab */}
			{result && activeSheet && (
				<div className="db-exports-section">
					<div className="db-exports-section-header">
						<h2>
							Preview <span className="db-exports-count">(first {PREVIEW_ROWS} rows of each tab)</span>
						</h2>
					</div>

					<Navigation
						links={sheets.map((sheet) => ({ id: sheet.name, label: sheet.name, count: sheet.rows.length }))}
						active={activeSheet.name}
						onClick={(tab) => setPreviewTab(tab)}
					/>

					<div className="db-exports-audit-preview">
						<Table
							className="db-exports-table"
							headings={previewHeaders}
							noResults={previewRows.length === 0}
							noResultsMessage={`No rows on the ${activeSheet.name} tab for this window`}>
							{previewRows.map((row, rowIndex) => (
								<tr key={rowIndex}>
									{previewHeaders.map((header) => (
										<td key={header}>{renderCell(row[header])}</td>
									))}
								</tr>
							))}
						</Table>
					</div>

					<p className="db-exports-audit-preview-note">
						Showing {Math.min(PREVIEW_ROWS, activeSheet.rows.length).toLocaleString()} of {activeSheet.rows.length.toLocaleString()} row{activeSheet.rows.length === 1 ? "" : "s"}
					</p>
				</div>
			)}
		</>
	)
}
